// apps/web/src/pages/vendas/fechamento/selecionar-modelo-fiscal-dialog.tsx — Fase 12, Plano 14 (FECH-02, R12-06, T-12-81).
// 'Resolver' de pendência de modelo fiscal: oferece SÓ versões do mesmo cliente+material do item (a lista vem da API),
// exige justificativa, mostra a origem escolhida e recalcula. A evidência do recebimento (Lote/NF) não é alterada.
import { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { RascunhoFiscalView, SelecionarModeloFiscalInput, VersaoModeloFiscalCompativel } from '@pieltec/contracts';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { FormMessage } from '../../../components/ui/form-message';
import { Label } from '../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { Textarea } from '../../../components/ui/textarea';
import { Skeleton } from '../../../components/ui/skeleton';
import { formatarData } from '../../../lib/vendas/formatacao';
import { AlertaConflitoPv } from '../detalhe/alerta-conflito-pv';
import { caminhoRascunho, escreverRascunho, MENSAGEM_FALHA_RECARGA_RASCUNHO } from './rascunho-api';

const JUSTIFICATIVA_MINIMA = 5;

interface SelecionarModeloFiscalDialogProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly retornoId: string;
  /** Versão do rascunho vista pelo usuário (lock otimista). */
  readonly versaoRascunho: number;
  readonly itemRef: string;
  readonly rotuloItem: string;
  readonly onSelecionado: (rascunho: RascunhoFiscalView) => void;
  /** Recarrega o rascunho; devolve se deu certo (só então o conflito é limpo). */
  readonly onRecarregar: () => Promise<boolean>;
}

export function SelecionarModeloFiscalDialog({
  aberto,
  onAbertoChange,
  retornoId,
  versaoRascunho,
  itemRef,
  rotuloItem,
  onSelecionado,
  onRecarregar,
}: SelecionarModeloFiscalDialogProps) {
  const [versoes, setVersoes] = useState<VersaoModeloFiscalCompativel[] | null>(null);
  const [erroCarga, setErroCarga] = useState(false);
  const [escolhida, setEscolhida] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [conflito, setConflito] = useState<string | null>(null);
  const [recarregando, setRecarregando] = useState(false);

  const carregar = useCallback(
    async (sinal: { vivo: boolean }) => {
      setErroCarga(false);
      setVersoes(null);
      try {
        const res = await fetch(`${caminhoRascunho(retornoId, '/modelos-fiscais')}?itemRef=${encodeURIComponent(itemRef)}`, { credentials: 'include' });
        if (!sinal.vivo) return;
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const lista = (await res.json()) as VersaoModeloFiscalCompativel[];
        if (sinal.vivo) setVersoes(lista);
      } catch {
        if (sinal.vivo) setErroCarga(true);
      }
    },
    [retornoId, itemRef],
  );

  useEffect(() => {
    if (!aberto) return;
    setEscolhida('');
    setJustificativa('');
    setErro(null);
    setConflito(null);
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [aberto, carregar]);

  const podeSalvar = escolhida !== '' && justificativa.trim().length >= JUSTIFICATIVA_MINIMA && !enviando;

  async function salvar(): Promise<void> {
    if (!podeSalvar) return;
    setErro(null);
    setConflito(null);
    setEnviando(true);
    try {
      const corpo: SelecionarModeloFiscalInput = {
        versao: versaoRascunho,
        itemRef,
        modeloFiscalVersaoId: escolhida,
        justificativa: justificativa.trim(),
      };
      const resultado = await escreverRascunho(retornoId, '/selecionar-modelo-fiscal', corpo);
      if (resultado.tipo === 'ok') {
        onAbertoChange(false);
        onSelecionado(resultado.view);
      } else if (resultado.tipo === 'conflito') {
        setConflito(resultado.message);
      } else {
        setErro(resultado.message);
      }
    } finally {
      setEnviando(false);
    }
  }

  async function recarregar(): Promise<void> {
    setRecarregando(true);
    try {
      if (await onRecarregar()) setConflito(null);
      else setErro(MENSAGEM_FALHA_RECARGA_RASCUNHO);
    } finally {
      setRecarregando(false);
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(proximo) => (enviando ? undefined : onAbertoChange(proximo))}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Resolver modelo fiscal — {rotuloItem}</DialogTitle>
          <DialogDescription>
            Escolha uma versão do modelo fiscal do mesmo cliente e material deste item. A evidência do recebimento não é alterada; a escolha
            fica registrada na auditoria e o rascunho é recalculado.
          </DialogDescription>
        </DialogHeader>
        {conflito && <AlertaConflitoPv mensagem={conflito} onRecarregar={() => void recarregar()} recarregando={recarregando} />}

        {erroCarga && (
          <div role="alert" className="flex flex-col items-start gap-2">
            <p className="text-sm text-rose-700">Não foi possível carregar as versões do modelo fiscal.</p>
            <Button type="button" variant="outline" size="sm" onClick={() => void carregar({ vivo: true })}>
              Tentar novamente
            </Button>
          </div>
        )}
        {!erroCarga && versoes === null && (
          <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando versões do modelo fiscal">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}
        {versoes !== null && versoes.length === 0 && (
          <p className="text-sm text-muted-foreground">Nenhuma versão de modelo fiscal cadastrada para este cliente e material.</p>
        )}
        {versoes !== null && versoes.length > 0 && (
          <RadioGroup value={escolhida} onValueChange={setEscolhida} aria-label="Versões do modelo fiscal" disabled={enviando}>
            {versoes.map((versao) => {
              const id = `modelo-fiscal-${versao.modeloFiscalVersaoId}`;
              return (
                <div key={versao.modeloFiscalVersaoId} className="flex items-start gap-3 rounded-md border px-3 py-2">
                  <RadioGroupItem id={id} value={versao.modeloFiscalVersaoId} className="mt-1" />
                  <Label htmlFor={id} className="flex flex-1 flex-col items-start gap-0.5 font-normal">
                    <span className="flex flex-wrap items-center gap-2 font-medium">
                      Versão {versao.versao}
                      {versao.emUso && <span className="rounded-full border border-blue-300 bg-blue-50 px-2 py-0.5 text-xs text-blue-800">Em uso</span>}
                      {versao.original && <span className="rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs text-slate-700">Original do recebimento</span>}
                    </span>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      Vigência {formatarData(versao.vigenciaInicio)} até {versao.vigenciaFim ? formatarData(versao.vigenciaFim) : 'sem fim'}
                    </span>
                    <span className="text-xs text-muted-foreground">{versao.motivo}</span>
                  </Label>
                </div>
              );
            })}
          </RadioGroup>
        )}

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="modelo-fiscal-justificativa">Justificativa da escolha (obrigatória)</Label>
          <Textarea
            id="modelo-fiscal-justificativa"
            value={justificativa}
            disabled={enviando}
            onChange={(e) => setJustificativa(e.target.value)}
            aria-describedby="modelo-fiscal-justificativa-ajuda"
          />
          <p id="modelo-fiscal-justificativa-ajuda" className="text-xs text-muted-foreground">
            Informe ao menos {JUSTIFICATIVA_MINIMA} caracteres. Fica registrada na auditoria.
          </p>
        </div>
        {erro && <FormMessage>{erro}</FormMessage>}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onAbertoChange(false)} disabled={enviando}>
            Voltar
          </Button>
          <Button type="button" onClick={() => void salvar()} disabled={!podeSalvar}>
            {enviando && <Loader2 className="animate-spin" aria-hidden="true" />}
            Usar esta versão
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
